import React, { memo, useEffect } from 'react';
import { useForceUpdate } from '../../hooks';
import i18n from '../../utilities/i18n';
import mediator from '../../utilities/mediator';
import {
  Button,
  IconBannersWarning,
  Text,
  VStack,
} from '@adc/parallax-component-library';

type Props = {
  reset?: () => void;
  errorMessage?: string;
};

function UnexpectedError({ reset, errorMessage }: Props) {
  const update = useForceUpdate();

  // If this screen is displayed because we cannot contact the server, i18n
  // will not be initialized. Fallback values are defined here.
  const title = i18n.t('UnexpectedError.title') || 'Something Went Wrong';
  const message =
    i18n.t(`UnexpectedError.code.${errorMessage}`) ||
    'Failed to load configuration from server.';
  const description =
    i18n.t('UnexpectedError.description') ||
    'Please refresh the page or try again later.';

  useEffect(() => {
    const sub = mediator.subscribe('langChange:success', () => {
      window.setTimeout(update, 0);
    });

    return () => {
      mediator.remove(sub.id);
    };
  }, [update]);

  return (
    <VStack gap="$2" flex={1} alignItems="center" justifyContent="center">
      <IconBannersWarning size="XL" accentColor="$support.warning.default" />
      <Text type="$h2.strong" textAlign="center">
        {title}
      </Text>
      <Text type="$bodyBase.default" textAlign="center">
        {errorMessage ? message : description}
      </Text>
      {/* The following will only be here when running the dev server */}
      {process.env.NODE_ENV !== 'production' && reset ? (
        <Button
          label="Dev only - Refresh component"
          variant="primary"
          size="small"
          onPress={reset}
          paddingTop="$2"
        />
      ) : null}
    </VStack>
  );
}

export default memo(UnexpectedError);
